import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const PricingRulesTab = () => {
  const [rules, setRules] = useState({
    yearlyDiscount: 20,
    upgradeProration: true,
    downgradeRefund: 'prorated',
    trialExpiration: 'auto-convert'
  });

  const [coupons, setCoupons] = useState([
    {
      id: 1,
      code: 'LAUNCH50',
      discount: 50,
      expiryDate: '2026-03-31',
      status: 'active'
    },
    {
      id: 2,
      code: 'GROWTH20',
      discount: 20,
      expiryDate: '2026-12-31',
      status: 'active'
    }
  ]);

  const [newCoupon, setNewCoupon] = useState({
    code: '',
    discount: '',
    expiryDate: ''
  });

  const [referralDiscount, setReferralDiscount] = useState(10);
  const [isSaving, setIsSaving] = useState(false);
  const [showCouponForm, setShowCouponForm] = useState(false);

  const handleRuleChange = (field, value) => {
    setRules({ ...rules, [field]: value });
  };

  const handleAddCoupon = () => {
    if (!newCoupon?.code || !newCoupon?.discount || !newCoupon?.expiryDate) {
      return;
    }

    const coupon = {
      id: Date.now(),
      code: newCoupon?.code?.toUpperCase(),
      discount: Number(newCoupon?.discount),
      expiryDate: newCoupon?.expiryDate,
      status: 'active'
    };

    setCoupons([...coupons, coupon]);
    setNewCoupon({ code: '', discount: '', expiryDate: '' });
    setShowCouponForm(false);
  };

  const handleDeleteCoupon = (couponId) => {
    setCoupons(coupons?.filter(c => c?.id !== couponId));
  };

  const handleSave = () => {
    setIsSaving(true);
    setTimeout(() => {
      console.log('Pricing rules saved:', { rules, coupons, referralDiscount });
      setIsSaving(false);
    }, 1000);
  };

  return (
    <div className="space-y-6">
      {/* General Pricing Rules */}
      <div className="bg-card border border-border rounded-lg p-4 md:p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Settings" size={20} className="text-primary" />
          General Pricing Rules
        </h3>
        <div className="space-y-4">
          <Input
            label="Yearly Discount Percentage"
            type="number"
            value={rules?.yearlyDiscount}
            onChange={(e) => handleRuleChange('yearlyDiscount', e?.target?.value)}
            description="Discount applied for yearly subscriptions"
          />
          <Input
            label="Referral Discount Percentage"
            type="number"
            value={referralDiscount}
            onChange={(e) => setReferralDiscount(e?.target?.value)}
            description="Discount for successful referrals"
          />
          <Checkbox
            checked={rules?.upgradeProration}
            onChange={() => handleRuleChange('upgradeProration', !rules?.upgradeProration)}
            label="Upgrade Proration"
            description="Enable prorated billing when users upgrade"
          />
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Downgrade Refund Policy
            </label>
            <select
              value={rules?.downgradeRefund}
              onChange={(e) => handleRuleChange('downgradeRefund', e?.target?.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              <option value="prorated">Prorated Refund</option>
              <option value="full">Full Refund</option>
              <option value="none">No Refund</option>
              <option value="credit">Account Credit</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Trial Expiration Logic
            </label>
            <select
              value={rules?.trialExpiration}
              onChange={(e) => handleRuleChange('trialExpiration', e?.target?.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              <option value="auto-convert">Auto Convert to Paid</option>
              <option value="manual-approval">Manual Approval Required</option>
              <option value="downgrade-free">Downgrade to Free Plan</option>
              <option value="suspend">Suspend Account</option>
            </select>
          </div>
        </div>
      </div>

      {/* Coupon Codes */}
      <div className="bg-card border border-border rounded-lg p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Icon name="Tag" size={20} className="text-primary" />
            Coupon Codes
          </h3>
          <Button
            variant="default"
            size="sm"
            onClick={() => setShowCouponForm(!showCouponForm)}
            iconName={showCouponForm ? 'X' : 'Plus'}
            iconPosition="left"
          >
            {showCouponForm ? 'Cancel' : 'Add Coupon'}
          </Button>
        </div>

        {/* Add Coupon Form */}
        {showCouponForm && (
          <div className="mb-4 p-4 bg-muted/30 border border-border rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <Input
                label="Coupon Code"
                type="text"
                value={newCoupon?.code}
                onChange={(e) => setNewCoupon({ ...newCoupon, code: e?.target?.value?.toUpperCase() })}
                placeholder="SUMMER2026"
              />
              <Input
                label="Discount %"
                type="number"
                value={newCoupon?.discount}
                onChange={(e) => setNewCoupon({ ...newCoupon, discount: e?.target?.value })}
                placeholder="25"
              />
              <Input
                label="Expiry Date"
                type="date"
                value={newCoupon?.expiryDate}
                onChange={(e) => setNewCoupon({ ...newCoupon, expiryDate: e?.target?.value })}
              />
            </div>
            <Button
              variant="default"
              size="sm"
              onClick={handleAddCoupon}
              iconName="Plus"
              iconPosition="left"
            >
              Create Coupon
            </Button>
          </div>
        )}

        {/* Coupons List */}
        <div className="space-y-3">
          {coupons?.map((coupon) => (
            <div key={coupon?.id} className="flex items-center justify-between p-4 bg-background border border-border rounded-lg">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <span className="font-mono font-semibold text-foreground">{coupon?.code}</span>
                  <span className="px-2 py-1 bg-success/10 text-success text-xs font-medium rounded-full">
                    {coupon?.discount}% OFF
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Expires: {new Date(coupon?.expiryDate)?.toLocaleDateString()}
                </p>
              </div>
              <button
                onClick={() => handleDeleteCoupon(coupon?.id)}
                className="p-2 hover:bg-destructive/10 rounded-md transition-smooth"
                title="Delete coupon"
              >
                <Icon name="Trash2" size={16} className="text-destructive" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          variant="default"
          size="lg"
          onClick={handleSave}
          loading={isSaving}
          iconName="Save"
          iconPosition="left"
        >
          Save & Activate Rules
        </Button>
      </div>
    </div>
  );
};

export default PricingRulesTab;